import { type ReactNode } from "react"

import { Box, Chip, type ChipProps, Typography } from "@mui/material"
import { Link } from "react-router-dom"

type LinkCellProps = {
  to: string
  children: ReactNode
  monospace?: boolean
}

export function LinkCell({ to, children, monospace }: LinkCellProps) {
  return (
    <Link
      to={to}
      style={{ textDecoration: "none", color: "inherit" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography
        variant="body2"
        fontWeight={500}
        color="primary"
        sx={{
          "&:hover": { textDecoration: "underline" },
          ...(monospace ? { fontFamily: "monospace" } : {}),
        }}
      >
        {children}
      </Typography>
    </Link>
  )
}

type DateCellProps = {
  value: string | Date | undefined | null
  stacked?: boolean
}

export function DateCell({ value, stacked }: DateCellProps) {
  if (!value) {
    return (
      <Typography variant="caption" color="text.secondary">
        -
      </Typography>
    )
  }

  const date = new Date(value)
  if (isNaN(date.getTime())) {
    return (
      <Typography variant="caption" color="text.secondary">
        Invalid date
      </Typography>
    )
  }

  const dateStr = date.toISOString().split("T")[0]
  const timeStr = date.toTimeString().split(" ")[0]?.slice(0, 5)

  if (stacked) {
    return (
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {dateStr}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25 }}
        >
          {timeStr}
        </Typography>
      </Box>
    )
  }

  return (
    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
      {dateStr}{" "}
      <Typography component="span" variant="caption" color="text.secondary">
        {timeStr}
      </Typography>
    </Typography>
  )
}

type StatusChipProps = {
  label: string
  color: NonNullable<ChipProps["color"]>
}

export function StatusChip({ label, color }: StatusChipProps) {
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      variant="outlined"
      sx={{ fontSize: "0.75rem", height: 24 }}
    />
  )
}

